// The Rankings "N players ranked" row + the Per 90/Total toggle. In the prototype it's
// a fixed sibling BETWEEN .module-sub-controls and .module-body (never inside the
// scrollable body), so it doesn't scroll out of view with the list — so, like
// SubControls, a converted module renders it via a portal into a real sibling element
// inserted just before the body. Mount it AFTER <SubControls> in the JSX so the sibling
// order stays [sub-controls, population-readout, body].

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface Props {
  mod: HTMLElement;
  body: HTMLElement;
  /** Null = no count, only the label — Stats on ?live=1 with no comparison group (T2-5:
   *  "No comparison for FAWSL 25/26"). */
  count: number | null;
  /** Show the Per 90/Total toggle (only for metrics that have a Total mode). */
  showToggle: boolean;
  /** "per90" | "total" — the button shows the currently-active mode. */
  mode: string;
  onToggle: () => void;
  /** The row's words — "players ranked" (Rankings), "players match" (Filter), or on ?live=1
   *  what the Stats tile's group is ("· full backs, FAWSL 25/26, 150+ min"). */
  label?: string;
  /** The button's words for each mode, when they aren't Per 90 / Total (a team's Stats tile:
   *  "For" / "Against", teams and matches 2026-10-30). */
  modeLabels?: Record<string, string>;
}

export function PopulationReadout({ mod, body, count, showToggle, mode, onToggle, label = "players ranked", modeLabels }: Props) {
  const [el] = useState(() => {
    const d = document.createElement("div");
    d.className = "population-readout";
    return d;
  });

  useEffect(() => {
    mod.insertBefore(el, body);
    return () => {
      el.remove();
    };
  }, [mod, body, el]);

  // The legacy toggles a `zero` class when nothing matches (styles.css dims the row).
  useEffect(() => {
    el.classList.toggle("zero", count !== null && count < 1);
  }, [el, count]);

  return createPortal(
    <>
      {count !== null && <span className="population-count">{count.toLocaleString()}</span>}
      <span className="population-label">{label}</span>
      {showToggle && (
        <button type="button" className="value-mode-toggle" data-mode={mode} onClick={onToggle}>
          {modeLabels ? modeLabels[mode] : mode === "total" ? "Total" : "Per 90"}
        </button>
      )}
    </>,
    el,
  );
}
